<template lang="pug">
  .panel-page-white
    .panel-white.body-1.mx-auto
      FormErrorAlert(v-show="forceErrorShow"
                      :message="message"
                      @dismiss="forceErrorShow = false")
      PageHeader Store Activity Report
        button.btn.btn-light(slot="extra" @click="clear") Clear Form
      form(autocomplete="off")
        p This report will display NPLEx transactions for a selected store.
        StoreActivityPanel(:show="stateShow || currentState === 'state'"
                            @update:show="stateShow = $event"
                            :show-edit="stateShowEdit"
                            @update:show-edit="stateShowEdit = $event")
          template(slot="header")
            h4.mb-0 State Selection: {{ stateValue }}
          template(slot="body")
            p Select State
            SingleSelect.single-select(v-model="stateValue"
                                        :entries="stateEntries"
                                        @clicked="clicked('state')")
        StoreActivityPanel.mt-3(:show="countyShow || currentState === 'county'"
                                @update:show="countyShow = $event"
                                :show-edit="countyShowEdit"
                                @update:show-edit="countyShowEdit = $event")
          template(slot="header")
            h4.mb-0 County Selection: {{ countyValue }}
          template(slot="body")
            p Select County
            SingleSelect.single-select(v-model="countyValue"
                                        :entries="countyNames"
                                        @clicked="clicked('county')")
        StoreActivityPanel.mt-3(:show="storeShow || currentState === 'store'"
                          @update:show="storeShow = $event"
                          :show-edit="storeShowEdit"
                          @update:show-edit="updateShowEdit = $event")
          template(slot="header")
            h4.mb-0
              | Store Selection: {{ storeValue }}
          template(slot="body")
            p Select Store
            SingleSelect.single-select(v-model="storeValue"
                                      :entries="storeNames"
                                      @clicked="clicked('store')")
        StoreActivityReportOptions.mt-3(:show="searchShow"
                                        :valid.sync="optionsValid"
                                        :form-submitted="submitted"
                                        :fromDate.sync="fromDate"
                                        :fromHour.sync="fromHour"
                                        :fromMinute.sync="fromMinute"
                                        :fromAmPm.sync="fromAmPm"
                                        :toDate.sync="toDate"
                                        :toHour.sync="toHour"
                                        :toMinute.sync="toMinute"
                                        :toAmPm.sync="toAmPm"
                                        :specifyTime.sync="specifyTime"
                                        :signatures.sync="signatures")
        button.btn.btn-primary.mt-3(@click.prevent="search"
                                    v-show="searchShow") Submit
</template>

<script>
import { loadForm } from '@/lib/formstore'
import { globalError, waitReport } from '@/lib/utils'
import {
  internalAdmin,
} from '@/lib/plugins/authorization'
import axios from '@/lib/axios'
import FormErrorAlert from '@/components/FormErrorAlert'
import PageHeader from '@/components/PageHeader'
import SingleSelect from '@/components/SingleSelect'
import states from '@/lib/states'
import stdmsg from '@/lib/stdmsg'
import StoreActivityPanel from '@/components/StoreActivityPanel'
import StoreActivityReportOptions from '@/components/StoreActivityReportOptions'
import { format, subDays, parse, differenceInCalendarDays } from 'date-fns/esm'

function justBeforeMidnight(dt) {
  dt.setHours(23)
  dt.setMinutes(59)
  dt.setSeconds(59)
  return dt
}

function midnight(dt) {
  dt.setHours(0)
  dt.setMinutes(0)
  dt.setSeconds(1)
  return dt
}
const startDate = justBeforeMidnight(subDays(new Date(), 31))
const endDate = midnight(new Date())

export default {
  name: 'StoreActivitySearch',
  data: function() {
    return {
      message: stdmsg.formError,
      optionsValid: true,
      forceErrorShow: false,
      stateEntries: this.getStates(),
      currentState: 'state',
      stateShow: true,
      stateShowEdit: false,
      stateValue: '',
      countyShow: false,
      countyShowEdit: false,
      countyValue: '',
      countyEntries: [],
      storeShow: false,
      storeShowEdit: false,
      storeValue: '',
      storeEntries: [],
      submitted: false,
      fromDate: format(startDate, 'MM/dd/yyyy'),
      fromHour: format(startDate, 'hh'),
      fromMinute: format(startDate, 'mm'),
      fromAmPm: format(startDate, 'a'),
      toDate: format(endDate, 'MM/dd/yyyy'),
      toHour: format(endDate, 'hh'),
      toMinute: format(endDate, 'mm'),
      toAmPm: format(endDate, 'a'),
      specifyTime: false,
      signatures: false,
    }
  },
  beforeMount: loadForm(
    'storeactivity',
    'stateShowEdit',
    'stateValue',
    'countyShowEdit',
    'countyValue',
    'countyEntries',
    'storeShowEdit',
    'storeValue',
    'storeEntries',
    'stateShow',
    'countyShow',
    'storeShow',
    'currentState',
    'fromDate',
    'fromHour',
    'fromMinute',
    'fromAmPm',
    'toDate',
    'toHour',
    'toMinute',
    'toAmPm',
    'specifyTime',
    'signatures',
  ),
  computed: {
    searchShow() {
      return this.currentState === 'complete'
    },
    storeByName: function() {
      return this.storeEntries.filter(e => e.name === this.storeValue)[0]
    },
    stateAbb() {
      const st = states.find(st => st.name === this.stateValue)
      return st ? st.abbreviation : ''
    },
    countyNames() {
      return this.countyEntries.map(co => co.countyName)
    },
    selectedCountyFipsCd() {
      const co = this.countyEntries.find(
        co => co.countyName === this.countyValue,
      )
      let code = co ? co.fipsCd : ''
      return code
    },
    storeNames() {
      return this.storeEntries.map(st => this.fullStoreName(st))
    },
    selectedStore() {
      return (
        this.storeEntries.find(
          st => this.fullStoreName(st) === this.storeValue,
        ) || {}
      )
    },
  },
  methods: {
    getStates() {
      const values = states.map(o => o.name)

      // filter out Oregon for non internal_admin users
      if (!internalAdmin()) {
        return values.filter(v => v !== "Oregon")
      }
      return values
    },
    inactiveName(storeInfo) {
      return storeInfo.inactive
        ? `${storeInfo.pharmacyName} - Inactive`
        : storeInfo.pharmacyName
    },
    storeAddress(st) {
      let address = ''
      if (st.address1) address += st.address1
      if (st.address2) address += ' ' + st.address2
      if (address && st.city) address += '. '
      if (st.city) address += st.city + ', '
      if (st.stateCd) address += st.stateCd
      if (st.postalCode) address += ' ' + st.postalCode
      if (address) return ' (' + address + ')'
      return address
    },
    fullStoreName(st) {
      return this.inactiveName(st) + this.storeAddress(st)
    },
    clicked: function(clickType) {
      this[`${clickType}Show`] = false
      this[`${clickType}ShowEdit`] = true

      if (clickType === this.currentState) {
        this.advanceState()
      }
    },
    advanceState: function() {
      if (this.currentState === 'state') {
        this.currentState = 'county'
      } else if (this.currentState === 'county') {
        this.currentState = 'store'
      } else if (this.currentState === 'store') {
        this.currentState = 'complete'
      }
    },
    search: function() {
      this.submitted = true
      this.$nextTick(() => {
        if (!this.optionsValid) {
          this.forceErrorShow = true
          scrollTo(0, 0)
          return
        } else {
          let fromDate = this.specifyTime
            ? `${this.fromDate} ${this.fromHour}:${this.fromMinute} ${this.fromAmPm}`
            : `${this.fromDate} 12:00 AM`
          let toDate = this.specifyTime
            ? `${this.toDate} ${this.toHour}:${this.toMinute} ${this.toAmPm}`
            : `${this.toDate} 11:59 PM`
          axios()
            .post('/api/store_activity/search', {
              pharmacySid: this.selectedStore.pharmacySid,
              fromDate,
              toDate,
              signatures: this.signatures,
            })
            .then(resp => {
              let id = resp.data.id
              waitReport(id, { name: 'store_activity_report', params: { id } })
            })
            .catch(err => globalError(err))
        }
      })
    },
    clear() {
      this.forceErrorShow = false
      this.currentState = 'state'
      this.stateShow = true
      this.stateShowEdit = false
      this.stateValue = ''
      this.countyShow = false
      this.countyShowEdit = false
      this.countyValue = ''
      this.countyEntries = []
      this.storeShow = false
      this.storeShowEdit = false
      this.storeValue = ''
      this.storeEntries = []
      this.fromDate = ''
      this.fromHour = ''
      this.fromMinute = ''
      this.fromAmPm = 'PM'
      this.toDate = ''
      this.toHour = ''
      this.toMinute = ''
      this.toAmPm = 'AM'
      this.specifyTime = false
      this.signatures = false
    },
  },
  watch: {
    stateValue: async function() {
      let { data: counties } = await axios({ loaderDelay: 2000 }).get(
        '/api/misc/counties',
        {
          params: { state: this.stateAbb },
        },
      )
      this.countyEntries = counties
    },
    countyValue: async function() {
      let { data: stores } = await axios({ loaderDelay: 2000 }).get(
        '/api/misc/stores',
        {
          params: { fips: this.selectedCountyFipsCd },
        },
      )
      this.storeEntries = stores
    },
  },
  components: {
    PageHeader,
    StoreActivityPanel,
    SingleSelect,
    FormErrorAlert,
    StoreActivityReportOptions,
  },
}
</script>

<style lang="scss" scoped>
.panel-white {
  width: 1007px;
}
</style>
